import React from 'react';
import { Link } from 'react-router';
import { Users, Calendar, Video, Archive } from 'lucide-react';
import type { LscLeague } from '../types';
import { NAV_LABEL_TOTAL_GAMES } from '../leagueNav';
import { lscPath } from '../routing';

interface LeagueStatusCardsProps {
  league: LscLeague;
  leagueId: string;
  matchCount?: number;
  memberCount?: number;
  memberDetail?: string;
  candidateCount?: number;
  vodSyncReadyDetail?: string;
  archiveCount?: number;
  isCommissioner: boolean;
}

const LeagueStatusCards: React.FC<LeagueStatusCardsProps> = ({
  league,
  leagueId,
  matchCount,
  memberCount,
  memberDetail,
  candidateCount,
  vodSyncReadyDetail,
  archiveCount,
  isCommissioner,
}) => {
  const cards = [
    {
      label: 'Current Members',
      value: memberCount ?? '—',
      detail: memberDetail,
      icon: Users,
      to: lscPath(`/leagues/${leagueId}/members`),
      enabled: true,
    },
    {
      label: NAV_LABEL_TOTAL_GAMES,
      value: matchCount ?? '—',
      icon: Calendar,
      to: lscPath(`/leagues/${leagueId}/matches`),
      enabled: true,
    },
    {
      label: 'VODs to review',
      value: isCommissioner ? candidateCount ?? '—' : '—',
      detail: isCommissioner ? vodSyncReadyDetail : undefined,
      icon: Video,
      to: lscPath(`/leagues/${leagueId}/vods`),
      enabled: isCommissioner,
    },
    {
      label: 'Archive',
      value: archiveCount ?? '—',
      icon: Archive,
      to: lscPath(`/leagues/${leagueId}/archive`),
      enabled: true,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ label, value, detail, icon: Icon, to, enabled }) =>
          enabled ? (
            <Link
              key={label}
              to={to}
              className="card hover:border-primary-500 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-secondary-400">{label}</span>
                <Icon className="h-4 w-4 text-primary-400" aria-hidden />
              </div>
              <p className="text-2xl font-semibold text-white">{value}</p>
              {detail && <p className="mt-1 text-xs text-secondary-500">{detail}</p>}
            </Link>
          ) : (
            <div key={label} className="card opacity-60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-secondary-400">{label}</span>
                <Icon className="h-4 w-4 text-secondary-500" aria-hidden />
              </div>
              <p className="text-sm text-secondary-500">Commissioner only</p>
            </div>
          )
        )}
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-2">{league.name}</h2>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-secondary-500">Competition</dt>
            <dd className="text-secondary-200">{league.game_label ?? league.game_key ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Visibility</dt>
            <dd className="text-secondary-200 capitalize">{league.visibility ?? 'members'}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Status</dt>
            <dd className="text-secondary-200 capitalize">{league.status ?? 'active'}</dd>
          </div>
          {league.league_tag && (
            <div>
              <dt className="text-secondary-500">League tag</dt>
              <dd className="text-secondary-200">{league.league_tag}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};

export default LeagueStatusCards;
